import type { FC, ReactElement, ReactNode } from "react"

import capitalize from "lodash-es/capitalize"

import { type TScreenBreakpoints, type TUserState, useScreenBreakpoints, useUser } from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import {
    ESpecialChar,
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    SpecialChars,
    Typography
} from "../typography"

const classes = {
    article: (): string =>
        buildCssClassName(
            "w-full",
            "h-[70px]",
            "bg-lightblue-300",
            "flex",
            "items-center",
            "justify-center",
            "flex-col",
            "fixed",
            "top-0",
            "z-[200]",
            "px-[5px]",
            "py-[5px]",
            "pointer-events-none",
            "select-none",
            "md:sticky",
            "md:h-10",
            "md:flex-row",
            "md:px-0",
            "md:py-0"
        ),
    text: (): string => "mb-0 md:text-base"
}

const VIEW_AS_BANNER_DOME_ELEMENT_ID: string = "view-as-banner"

const ViewAsBanner: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const { user }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    function renderUserInfo(): ReactNode {
        return (
            <>
                <Typography
                    tag={ETypographyTag.Paragraph}
                    size={ETypographySize.Small}
                    alignment={ETypographyAlignment.Center}
                    className={classes.text()}
                    text={
                        <>
                            <span>{capitalize(user?.productType ?? "")}</span>
                            {SpecialChars[ESpecialChar.EmptySpace]}
                            <span>User</span>
                            {SpecialChars[ESpecialChar.EmptySpace]}
                        </>
                    }
                />
                <Typography
                    tag={ETypographyTag.Span}
                    size={ETypographySize.Small}
                    className={classes.text()}
                    text={
                        <>
                            {SpecialChars[ESpecialChar.OpeningBracket]}
                            {user?.firstName}
                            {SpecialChars[ESpecialChar.EmptySpace]}
                            {user?.lastName}
                        </>
                    }
                />
            </>
        )
    }

    return (
        <article className={classes.article()} id={VIEW_AS_BANNER_DOME_ELEMENT_ID}>
            <Typography
                tag={ETypographyTag.Paragraph}
                size={ETypographySize.Small}
                color={ETypographyColor.Accent}
                weight={ETypographyWeight.Bold}
                className={classes.text()}
                text={
                    <>
                        {t("participantSide.fakeParticipantMode.banner.headingLabel")}
                        {SpecialChars[ESpecialChar.Colon]}
                        {SpecialChars[ESpecialChar.EmptySpace]}
                    </>
                }
            />
            {breakpoints.xs || breakpoints.sm ? <div>{renderUserInfo()}</div> : <>{renderUserInfo()}</>}
        </article>
    )
}

export { ViewAsBanner, VIEW_AS_BANNER_DOME_ELEMENT_ID }
